import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, InputGroup, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";

import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import SuccsessMsgModal from "../../components/SuccessMsgModal";
import ChangeRequestCard from "./ChangeRequestCard";
import ChangeRequestDetailModal from "./ChangeRequestDetailModal";
import {
  CHANGE_REQUEST_COLUMNS,
  STATUS_FILTERS,
  departmentLabel,
} from "./changeRequestConfig";
import "./ChangeRequests.css";

/**
 * Change Requests: one column per module (Products, Orders, Customers,
 * Vendors, Contract Vehicles). Cards are not draggable; a request moves only
 * through approve / reject, and stays visible under Approved afterwards.
 * `?id=<request>` (used in the approval e-mails) opens that request.
 */
function ChangeRequestsBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  /* Open on what still needs someone: approved / rejected / cancelled are a filter away. */
  const [status, setStatus] = useState("PENDING");
  const [department, setDepartment] = useState("ALL");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  const openId = searchParams.get("id");

  const { isLoading, postData: fetchList } = usePostRequest(
    API_ENDPOINTS.CHANGE_REQUEST_LIST,
  );

  const load = useCallback(() => {
    fetchList({ status }, (res) => setItems(res?.data?.items || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const departments = useMemo(
    () => [...new Set(items.map((item) => item.requester_department).filter(Boolean))].sort(),
    [items],
  );

  /* Search and department are applied here — the list is small and already loaded. */
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      if (department !== "ALL" && item.requester_department !== department) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [
        item.module_ref,
        item.requester?.name,
        item.requester?.username,
        item.requester_department,
        item.order_payment_type,
        item.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [items, department, search]);

  const openRequest = (request) => setSearchParams({ id: request.id });
  const closeRequest = () => setSearchParams({});

  const handleDone = (message) => {
    closeRequest();
    setConfirmation(message);
    load();
  };

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Change Requests</h2>
      </div>

      <Form className="cr-toolbar" onSubmit={(event) => event.preventDefault()}>
        <InputGroup className="cr-search">
          <Form.Control
            className="bg-light shadow-none"
            placeholder="Search anything..."
            value={search}
            aria-label="Search change requests"
            autoComplete="off"
            onChange={(event) => setSearch(event.target.value)}
          />
          <InputGroup.Text className="bg-white border-start-0">
            <IconSearch stroke={2} size={18} />
          </InputGroup.Text>
        </InputGroup>

        <Form.Select
          className="cr-select"
          value={status}
          aria-label="Status"
          onChange={(event) => setStatus(event.target.value)}
        >
          {STATUS_FILTERS.map((filter) => (
            <option key={filter.key} value={filter.key}>
              {filter.label}
            </option>
          ))}
        </Form.Select>

        <Form.Select
          className="cr-select"
          value={department}
          aria-label="Department"
          onChange={(event) => setDepartment(event.target.value)}
        >
          <option value="ALL">All Department</option>
          {departments.map((name) => (
            <option key={name} value={name}>
              {departmentLabel(name)}
            </option>
          ))}
        </Form.Select>
      </Form>

      <div className="cr-board">
        {CHANGE_REQUEST_COLUMNS.map((column) => {
          const columnItems = visible.filter((item) => item.module_type === column.key);
          return (
            <section className={`cr-column cr-column-${column.tone}`} key={column.key} aria-label={column.label}>
              <div className="cr-column-head">
                <span className="cr-column-title">
                  <span className="cr-column-dot" aria-hidden="true" />
                  {column.label}
                </span>
                <span className="cr-column-count">
                  {String(columnItems.length).padStart(2, "0")}
                </span>
              </div>

              <div className="cr-column-body">
                {isLoading && items.length === 0 ? (
                  <div className="cr-column-empty">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : columnItems.length === 0 ? (
                  <div className="cr-column-empty">No requests</div>
                ) : (
                  columnItems.map((request) => (
                    <ChangeRequestCard
                      key={request.id}
                      request={request}
                      tone={column.tone}
                      onOpen={openRequest}
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>

      <ChangeRequestDetailModal
        requestId={openId}
        onHide={closeRequest}
        onDone={handleDone}
      />

      <SuccsessMsgModal
        show={Boolean(confirmation)}
        onHide={() => setConfirmation(null)}
        title="Done"
        message={confirmation}
        btntext="Close"
      />
    </>
  );
}

export default ChangeRequestsBoard;
